import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toastr from "toastr";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { FiDownload } from "react-icons/fi";
import { LuFilterX, LuRefreshCcw } from "react-icons/lu";
import { PaperComponent, statusBar } from "../../utils";
import StyledTooltip from "../shared/toolTip/StyledTooltip";
import StatusChip from "../shared/StatusChip";
import {
  downloadReportBatchAPI,
  getReportBatchByIdAPI,
  getReportBatchesAPI,
  getReportBatchQueuePositionAPI,
} from "../apis/pgapis";
import COLORS from "../styles/COLORS";

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
};

const getStatusMeta = (status) => {
  const normalizedStatus = status || "";

  if (normalizedStatus === "COMPLETED") {
    return { label: normalizedStatus.replaceAll("_", " "), color: "green" };
  }

  if (normalizedStatus === "COMPLETED_WITH_ERRORS") {
    return { label: normalizedStatus.replaceAll("_", " "), color: "orange" };
  }

  if (normalizedStatus === "FAILED" || normalizedStatus === "QUEUE_FAILED") {
    return { label: normalizedStatus.replaceAll("_", " "), color: "red" };
  }

  return { label: normalizedStatus.replaceAll("_", " ") || "UNKNOWN", color: "blue" };
};

const isBatchExpired = (expiresOn) => {
  if (!expiresOn) {
    return false;
  }

  const expiryDate = new Date(expiresOn);

  if (Number.isNaN(expiryDate.getTime())) {
    return false;
  }

  return expiryDate <= new Date();
};

const BatchReportStatusModal = ({ open, onClose, packageId, revision }) => {
  const { t } = useTranslation();
  const pkg = useSelector((state) => state.ctrl.pkg);
  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingBatchIds, setDownloadingBatchIds] = useState([]);
  const project_data = useSelector((state) => state.ctrl.data);
  const package_data = useSelector((state) => state.ctrl.pkg);

  const resolvedPackageId = packageId || pkg?.package_id;
  const resolvedRevision = revision || pkg?.active_revision;

  const defaultColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    editable: false,
    sortingOrder: ["asc", "desc", " "],
    filterParams: {
      buttons: ["apply", "cancel"],
      closeOnApply: true,
    },
    menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
  };

  const clearFilters = () => {
    gridApi?.setFilterModel(null);
  };

  const buildBatchDownloadFileName = (batch) => {
    const batchId = batch?.batch_id;

    const projectName = project_data?.project_name;
    const packageName = package_data?.package_name;

    if (projectName && packageName && batchId) {
      return `MyProject Workspace_${projectName}_${packageName}_${batchId}.zip`;
    }

    return batch?.zip_file_name;
  };

  const handleDownloadBatch = useCallback(
    async (batch) => {
      if (!batch?.batch_id) {
        return;
      }

      const batchId = batch?.batch_id;

      setDownloadingBatchIds((prev) => (prev.includes(batchId) ? prev : [...prev, batchId]));

      try {
        const response = await downloadReportBatchAPI(batch.batch_id);
        const fileName = buildBatchDownloadFileName(batch);

        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error(`Failed to download report batch ${batch.batch_id}`, error);
        toastr.error(t("Failed to download report batch."));
      } finally {
        setDownloadingBatchIds((prev) => prev.filter((id) => id !== batchId));
      }
    },
    [t],
  );

  const statusRenderer = useCallback(
    (params) => {
      const batch = params?.data;
      const batchId = batch?.batch_id;
      const { label, color } = getStatusMeta(batch?.status);
      const isDownloadAvailable = Boolean(batch?.zip_file_name) && !isBatchExpired(batch?.expires_on);
      const isCurrentlyDownloading = downloadingBatchIds.includes(batchId);

      return (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <StatusChip label={label} color={color} />
          {isDownloadAvailable ? (
            isCurrentlyDownloading ? (
              <CircularProgress size={15} style={{ color: COLORS.blue }} />
            ) : (
              <button
                type="button"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: COLORS.blue,
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDownloadBatch(batch);
                }}
              >
                <FiDownload size={15} />
              </button>
            )
          ) : null}
        </div>
      );
    },
    [downloadingBatchIds, handleDownloadBatch, t],
  );

  const tagCountRenderer = useCallback((params) => {
    const batch = params?.data || {};

    const counts = [
      { label: "Total", value: batch?.total_tags, color: COLORS.blue, bg: COLORS.lightBlue },
      { label: "Processed", value: batch?.processed_tags, color: COLORS.blue, bg: COLORS.lightBlue },
      { label: "Success", value: batch?.success_tags, color: COLORS.green, bg: COLORS.lightGreen },
      { label: "Failed", value: batch?.failed_tags, color: COLORS.red, bg: COLORS.lightRed },
      { label: "Errors", value: batch?.completed_with_errors_tags, color: COLORS.orange, bg: COLORS.lightOrange },
    ];

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "5px", height: "100%", overflow: "hidden" }}>
        {counts.map(({ label, value, color, bg }) => (
          <span
            key={label}
            style={{
              background: bg,
              borderRadius: "4px",
              padding: "1px 6px",
              fontSize: "11px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flex: "1 1 0",
              textAlign: "center",
              color: color,
            }}
          >
            {value ?? 0}
          </span>
        ))}
      </div>
    );
  }, []);

  const queuePositionRenderer = useCallback((params) => {
    const { batches_ahead, tags_ahead } = params?.data || {};
    if (batches_ahead == null && tags_ahead == null) return null;
    const items = [
      { label: "Batches", value: batches_ahead ?? 0, color: COLORS.blue, bg: COLORS.lightBlue },
      { label: "Tags", value: tags_ahead ?? 0, color: COLORS.blue, bg: COLORS.lightBlue },
    ];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "5px", height: "100%", overflow: "hidden" }}>
        {items.map(({ label, value, color, bg }) => (
          <span
            key={label}
            title={label}
            style={{
              background: bg,
              borderRadius: "4px",
              padding: "1px 6px",
              fontSize: "11px",
              whiteSpace: "nowrap",
              flex: "1 1 0",
              textAlign: "center",
              color,
            }}
          >
            {label}: {value}
          </span>
        ))}
      </div>
    );
  }, []);

  const detailColumnDefs = useMemo(
    () => [
      { headerName: "Tag Number", field: "tag_number", minWidth: 150 },
      { headerName: "Technology", field: "technology_name", minWidth: 160 },
      { headerName: "Product Description", field: "product_description", minWidth: 160 },
      {
        headerName: "Requested Report Types",
        field: "requested_report_types",
        minWidth: 220,
        valueGetter: (params) => (params?.data?.requested_report_types || []).join(", "),
      },
      { headerName: "Status", field: "status", width: 200, cellRenderer: statusRenderer },
      { headerName: "Error Message", field: "error_message", minWidth: 220, flex: 1 },
      {
        headerName: "Successful Files",
        field: "result_summary.successful_files",
        minWidth: 220,
        valueGetter: (params) => (params?.data?.result_summary?.successful_files || []).join(", "),
      },
      {
        headerName: "Missing Reports",
        field: "result_summary.missing_reports",
        minWidth: 180,
        valueGetter: (params) => (params?.data?.result_summary?.missing_reports || []).join(", "),
      },
      {
        headerName: "Last Updated On",
        field: "last_updated_on",
        minWidth: 170,
        valueGetter: (params) => formatDateTime(params?.data?.last_updated_on),
      },
    ],
    [statusRenderer],
  );

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        defaultColDef: {
          sortable: true,
          resizable: true,
          filter: true,
          editable: false,
        },
        columnDefs: detailColumnDefs,
        headerHeight: 30,
        rowHeight: 26,
        domLayout: "normal",
        suppressRowClickSelection: true,
        overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading Tag Details...</span>',
      },
      getDetailRowData: async (params) => {
        const batchId = params?.data?.batch_id;

        if (!batchId) {
          params.successCallback([]);
          return;
        }

        try {
          const batchDetail = await getReportBatchByIdAPI(batchId);
          params.successCallback(Array.isArray(batchDetail?.tags) ? batchDetail.tags : []);
        } catch (error) {
          console.error(`Failed to load report batch ${batchId}`, error);
          toastr.error(t("Failed to load report batch details."));
          params.successCallback([]);
        }
      },
    }),
    [detailColumnDefs, t],
  );

  const isRowMaster = useCallback((data) => !!data?.batch_id, []);

  const columnDefs = [
    {
      headerName: "Batch ID",
      field: "batch_id",
      width: 130,
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        suppressCount: true,
      },
    },
    {
      headerName: "Group By",
      field: "group_by",
      width: 120,
      valueGetter: (params) => params?.data?.group_by?.toUpperCase(),
    },
    { headerName: "Status", field: "status", width: 240, cellRenderer: statusRenderer },
    {
      headerName: "Tags (Total/Processed/Success/Failed/Completed with Errors)",
      field: "total_tags",
      minWidth: 200,
      flex: 1,
      cellRenderer: tagCountRenderer,
    },
    { headerName: "Failure Summary", field: "failure_summary", minWidth: 220, flex: 1 },
    {
      headerName: "Queue Position",
      field: "batches_ahead",
      width: 200,
      cellRenderer: queuePositionRenderer,
    },
    { headerName: "Submitted By", field: "submitted_by", minWidth: 220 },
    {
      headerName: "Submitted On",
      field: "submitted_on",
      minWidth: 170,
      valueGetter: (params) => formatDateTime(params?.data?.submitted_on),
    },
    {
      headerName: "Last Updated On",
      field: "last_updated_on",
      minWidth: 170,
      valueGetter: (params) => formatDateTime(params?.data?.last_updated_on),
    },
  ];

  const loadBatchData = async () => {
    if (!resolvedPackageId || !resolvedRevision) {
      setRowData([]);
      return;
    }

    try {
      setIsLoading(true);
      const data = await getReportBatchesAPI({
        package_id: resolvedPackageId,
        revision: resolvedRevision,
        limit: 100,
      });

      const sorted = Array.isArray(data) ? [...data].sort((a, b) => Number(b.batch_id) - Number(a.batch_id)) : [];
      const STATUS = new Set(["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "QUEUE_FAILED", "RUNNING", "ZIPPING"]);

      const queued = sorted.filter((b) => b?.batch_id && !STATUS.has(b.status));
      const queuePositions = await Promise.all(
        queued.map((b) =>
          getReportBatchQueuePositionAPI(b.batch_id)
            .then((pos) => ({ batch_id: b.batch_id, ...pos }))
            .catch(() => ({ batch_id: b.batch_id })),
        ),
      );
      const posMap = Object.fromEntries(queuePositions.map((p) => [p.batch_id, p]));

      setRowData(sorted.map((b) => (posMap[b.batch_id] ? { ...b, ...posMap[b.batch_id] } : b)));
    } catch (error) {
      console.error("Failed to load report batches", error);
      toastr.error(t("Failed to load report batch data."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadBatchData();
    }
  }, [open, resolvedPackageId, resolvedRevision]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperComponent={PaperComponent}
      className="muiForm-Modal"
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle
        className="model-title mb-0 text-align-center"
        id="draggable-dialog-title"
        style={{ display: "flex" }}
      >
        <div className="header-row">
          <div>
            Batch Report Status
            <StyledTooltip title="Refresh" placement="bottom" disableInteractive>
              <span className={isLoading ? "refresh-report disabled" : "refresh-report"} onClick={loadBatchData}>
                <LuRefreshCcw className={isLoading ? "animatonSVG" : ""} />
              </span>
            </StyledTooltip>
          </div>
        </div>

        <StyledTooltip title={t("Clear Filters")} placement="bottom" disableInteractive style={{ padding: "3px 0px" }}>
          <label>
            <LuFilterX className="tag-grid-head-cntrl mx-2" onClick={clearFilters} style={{ cursor: "pointer" }} />
          </label>
        </StyledTooltip>
      </DialogTitle>
      <DialogContent>
        <div className="ag-theme-alpine marginRow muiForm mb-2 pt-3" style={{ height: "68vh" }}>
          {isLoading && <LinearProgress />}
          <AgGridReact
            statusBar={statusBar}
            masterDetail={true}
            isRowMaster={isRowMaster}
            rowData={rowData}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            detailCellRendererParams={detailCellRendererParams}
            detailRowHeight={360}
            headerHeight={30}
            rowHeight={26}
            onGridReady={(params) => setGridApi(params?.api)}
            overlayLoadingTemplate={
              '<div class="loadingx" style="margin: 7em"></div> <span class="ag-overlay-loading-center " style="font-size: 18px; z-index: 100000"> Loading Rows ...</span>'
            }
          />
        </div>
      </DialogContent>
      <DialogActions className="model-actions">
        <button className="cnbtn btn cancel-btn" variant="outlined" onClick={onClose}>
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchReportStatusModal;
